import type {
    InboxWorkflowCellComponent,
    InboxWorkflowTableStepConfig,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import AssignmentAppointmentComparisonCell from "./_modules/assignment-appointment-comparison-cell"
import AssignmentExistingAppointmentCell from "./_modules/assignment-existing-appointment-cell"
import AssignmentPropertyContextCell from "./_modules/assignment-property-context-cell"
import AssignmentRequesterContactCell from "./_modules/assignment-requester-contact-cell"
import AssignmentRequesterContactDataCell from "./_modules/assignment-requester-contact-data-cell"
import AssignmentRequesterHintsCell from "./_modules/assignment-requester-hints-cell"
import AssignmentWorkOrderSelectCell from "./_modules/assignment-work-order-select-cell"
import AssignmentWorkOrderSummaryCell from "./_modules/assignment-work-order-summary-cell"
import AssignWorkOrderRowActions from "./_actions/AssignWorkOrderRowActions.actions"
import { defaultAssignWorkOrderDraft, validateAssignWorkOrderDraft } from "./_logic/drafts"
import { assignWorkOrderPayload } from "./_logic/payload"
import { buildWorkOrderAssignmentRows } from "./_logic/tableRows"
import type {
  AssignWorkOrderCommitPayload,
  AssignWorkOrderDraft,
  AssignWorkOrderPreviewPayload,
  WorkOrderAssignmentRow,
} from "./contract"
import { parseAssignWorkOrderPreview } from "./_logic/preview"

export const assignWorkOrderCells: Record<string, InboxWorkflowCellComponent> = {
  assignmentAppointmentComparison: AssignmentAppointmentComparisonCell,
  assignmentExistingAppointment: AssignmentExistingAppointmentCell,
  assignmentProperty: AssignmentPropertyContextCell,
  assignmentRequesterContact: AssignmentRequesterContactCell,
  assignmentRequesterContactData: AssignmentRequesterContactDataCell,
  assignmentRequesterHints: AssignmentRequesterHintsCell,
  assignmentWorkOrderSelect: AssignmentWorkOrderSelectCell,
  assignmentWorkOrderSummary: AssignmentWorkOrderSummaryCell,
}

export type AssignWorkOrderStepOptions = {
  includeRequestedAppointmentColumn?: boolean
}

const assignWorkOrderColumns = (_options: AssignWorkOrderStepOptions = {}) => [
  {
    id: "workOrder",
    label: "Zugeordneter Auftrag",
    cellKey: "assignmentWorkOrderSelect",
    isPrimary: true,
  },
  {
    id: "requestedAppointment",
    label: "Terminvorschlag",
    cellKey: "assignmentAppointmentComparison",
  },
  {
    id: "existingAppointment",
    label: "Ursprünglicher Termin",
    cellKey: "assignmentExistingAppointment",
  },
  { id: "propertyContext", label: "Liegenschaft", cellKey: "assignmentProperty" },
  { id: "requesterContact", label: "Ansprechpartner", cellKey: "assignmentRequesterContact" },
  { id: "requesterContactData", label: "Kontaktdaten", cellKey: "assignmentRequesterContactData" },
  { id: "requesterHints", label: "Hinweise", cellKey: "assignmentRequesterHints" },
]

export const createAssignWorkOrderStep = (
  options: AssignWorkOrderStepOptions = {},
): InboxWorkflowTableStepConfig<
  WorkOrderAssignmentRow,
  AssignWorkOrderDraft,
  AssignWorkOrderCommitPayload,
  AssignWorkOrderPreviewPayload
> => ({
  kind: "table",
  preview: {
    kind: "unclear_appointment_feedback.assign_work_order.standard_table",
    parse: parseAssignWorkOrderPreview,
    errorMessage: "Auftragsauswahl konnte nicht geladen werden.",
  },
  columns: assignWorkOrderColumns(options),
  buildRows: ({ workflow, step, rowOutcomes, stepPreview }) =>
    buildWorkOrderAssignmentRows({ workflow, step, rowOutcomes, stepPreview }),
  draft: {
    create: defaultAssignWorkOrderDraft,
    validate: validateAssignWorkOrderDraft,
  },
  rowActions: AssignWorkOrderRowActions,
  buildPayload: assignWorkOrderPayload,
  commit: {
    label: "Aufträge übernehmen",
    successMessage: "Aufträge wurden übernommen.",
    partialMessage: (result) =>
      result.failed_count
        ? `${result.success_count} Aufträge übernommen, ${result.failed_count} fehlgeschlagen.`
        : `${result.success_count} Aufträge übernommen.`,
  },
})
